import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { useTranslation } from '../../../core/hooks/useTranslation';
import { RemoteImage } from '../../../core/components/RemoteImage';
import { resolveImageUrl } from '../../../core/utils/image';

interface ProjectGalleryMiniProps {
    images: string[];
}

export const ProjectGalleryMini = ({ images }: ProjectGalleryMiniProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const { isRTL } = useTranslation();

    const openLightbox = (index: number) => {
        setCurrentIndex(index);
        setIsOpen(true);
    };

    const nextImage = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    return (
        <div className="mb-12">
            <h3 className="text-xl font-display font-bold text-secondary mb-4">Gallery</h3>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x">
                {images.map((img, idx) => (
                    <div
                        key={idx}
                        onClick={() => openLightbox(idx)}
                        className="w-32 h-32 flex-shrink-0 rounded-xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity border border-gray-100 snap-start relative group"
                    >
                        <RemoteImage src={img} alt={`Gallery ${idx}`} containerClassName="absolute inset-0 w-full h-full" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <Maximize2 className="w-6 h-6 text-white drop-shadow-md" />
                        </div>
                    </div>
                ))}
            </div>

            {/* Lightbox Modal */}
            {isOpen && (
                <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setIsOpen(false)}>

                    <button
                        onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                        className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors z-[110]"
                    >
                        <X className="w-8 h-8" />
                    </button>

                    <button
                        onClick={prevImage}
                        className={`absolute left-4 min-w-[3rem] h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-[110] ${isRTL ? 'right-4 left-auto' : ''}`}
                    >
                        {isRTL ? <ChevronRight className="w-6 h-6" /> : <ChevronLeft className="w-6 h-6" />}
                    </button>

                    <div className="relative max-w-5xl max-h-[85vh] w-full" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={resolveImageUrl(images[currentIndex])}
                            alt="Full View"
                            className="w-full h-full object-contain rounded-lg shadow-2xl"
                        />
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 px-4 py-2 rounded-full text-white text-sm backdrop-blur-md">
                            {currentIndex + 1} / {images.length}
                        </div>
                    </div>

                    <button
                        onClick={nextImage}
                        className={`absolute right-4 min-w-[3rem] h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-[110] ${isRTL ? 'left-4 right-auto' : ''}`}
                    >
                        {isRTL ? <ChevronLeft className="w-6 h-6" /> : <ChevronRight className="w-6 h-6" />}
                    </button>
                </div>
            )}
        </div>
    );
};
